import React from 'react';
import QRCode from 'react-qr-code';
import { Alert, Button, Divider, Input, Space, Typography } from 'antd';
import { CodeOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AuthenticatorSetupModel, LoginResponseModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';

type Props = {
  loginResponse: LoginResponseModel;
  onDone: () => void;
};

/**
 * Enrôlement de l'application d'authentification (TOTP), proposé après une connexion par e-mail ou SMS quand le
 * compte exige la double authentification : QR code, confirmation par un premier code, codes de secours affichés
 * une seule fois. Même parcours que le Caissier web.
 */
export default function AuthenticatorEnrollment({ loginResponse, onDone }: Props) {
  const { t } = useTranslation();
  const auth = { headers: { Authorization: `Bearer ${loginResponse.accessToken}` } };
  const [loading, setLoading] = React.useState(false);
  const [setup, setSetup] = React.useState<AuthenticatorSetupModel>();
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string>();
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>();

  const begin = () => {
    setLoading(true);
    setError(undefined);
    apiSdk.AuthenticatorService.beginEnrollment(auth)
      .then(res => (res.success ? setSetup(res) : setError(t(res.message || UNKNOWN_ERROR_MESSAGE))))
      .catch(() => setError(t(UNKNOWN_ERROR_MESSAGE)))
      .finally(() => setLoading(false));
  };

  const confirm = () => {
    setLoading(true);
    setError(undefined);
    apiSdk.AuthenticatorService.confirmEnrollment({ body: { code } }, auth)
      .then(res => (res.success ? setRecoveryCodes(res.recoveryCodes || []) : setError(t(res.message || UNKNOWN_ERROR_MESSAGE))))
      .catch(() => setError(t(UNKNOWN_ERROR_MESSAGE)))
      .finally(() => setLoading(false));
  };

  return (
    <div style={{ padding: 15 }}>
      <div style={{ textAlign: 'center' }}>
        <SafetyOutlined style={{ fontSize: 48, color: '#0a8ca3' }} />
        <h2>{t('loginPage_AuthenticatorTitle')}</h2>
      </div>
      <Divider />
      {error ? <Alert type="warning" message={error} style={{ marginBottom: 10 }} /> : null}
      {recoveryCodes ? (
        <div>
          <p>{t('loginPage_RecoveryCodesHint')}</p>
          <Space wrap style={{ marginBottom: 20 }}>
            {recoveryCodes.map(x => (
              <Typography.Text key={x} code copyable style={{ fontSize: 16 }}>
                {x}
              </Typography.Text>
            ))}
          </Space>
          <div style={{ textAlign: 'center' }}>
            <Button type="primary" className="login-form-button" onClick={onDone}>
              {t('loginPage_Continue')}
            </Button>
          </div>
        </div>
      ) : setup ? (
        <div>
          <p>{t('loginPage_AuthenticatorScan')}</p>
          <div style={{ textAlign: 'center', background: '#fff', padding: 12 }}>
            <QRCode value={setup.authenticatorUri || ''} size={200} />
          </div>
          <p style={{ marginTop: 10 }}>
            {t('loginPage_AuthenticatorManualKey')} <Typography.Text code copyable>{setup.sharedKey}</Typography.Text>
          </p>
          <Input
            prefix={<CodeOutlined />}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={e => setCode(e.target.value)}
            onPressEnter={confirm}
          />
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Button type="primary" className="login-form-button" loading={loading} disabled={code.trim().length < 6} onClick={confirm}>
              {t('loginPage_Verify')}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p>{t('loginPage_AuthenticatorRequired')}</p>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Button type="primary" className="login-form-button" loading={loading} onClick={begin}>
              {t('loginPage_AuthenticatorStart')}
            </Button>
          </div>
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <Button type="link" onClick={onDone}>
              {t('loginPage_Later')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
